import { AccessRights } from 'src/app/features/role/user-role/user-role'
import { BulkAction } from 'src/app/shared/interfaces'

import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core'
import { DataTable } from '@pascalhonegger/ng-datatable'

import { Router } from '@angular/router'
import { UntilDestroy } from '@ngneat/until-destroy'
import { Dictionary } from '@ngrx/entity'
import { Store } from '@ngrx/store'
import { Observable, Subject, combineLatest, of } from 'rxjs'
import { map } from 'rxjs/operators'
import moment from 'moment-mini'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { Member } from 'src/app/features/member/member'
import { memberSelectors } from 'src/app/features/member/member.selectors'
import { MessageService } from 'src/app/features/message/message.service'
import { Organization } from 'src/app/features/organization/organization'
import { organizationSelectors } from 'src/app/features/organization/organization.selectors'
import { Product } from 'src/app/features/product/product'
import { productSelectors } from 'src/app/features/product/product.selectors'
import {
  SerieProposal,
  SerieProposalStatus,
  SerieProposalType
} from '../serie-proposal'
import { serieProposalSelectors } from '../serie-proposal.selectors'
import { SerieProposalService } from '../serie-proposal.service'
import { serieProposalItemSelectors } from '../serie-proposal-item/serie-proposal-item.selectors'
import { SerieProposalItemStatus } from '../serie-proposal-item/serie-proposal-item'
@UntilDestroy()
@Component({
  selector: 'serie-proposal-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss']
})
export class SerieProposalTableComponent {
  @Input() tableData: SerieProposal[]
  @Input() bulkActions: BulkAction[]
  @Input() isLoading: boolean
  @Input() total: number
  @Input() showTotal = false
  @Input() pageCount: number
  @Input() activePage: number = 1
  @Input() limitPage: number = 25
  @Output() onBulkAction = new EventEmitter<any>()
  @Output() onClickRow = new EventEmitter<any>()
  @Output() onSelectLimit = new EventEmitter<any>()
  @Output() onSearchTerm = new EventEmitter<any>()
  @Output() onSelectedPage = new EventEmitter<any>()
  @Output() onSorted = new EventEmitter<any>()
  @ViewChild('dataTable', { static: true }) dataTable: DataTable
  public sorted: string = 'desc'
  public field: string = 'id'
  public serieProposalType = SerieProposalType
  public filterTerm: string = ''
  public allCheckedToggle: boolean = false
  public checkedIds: Map<number, boolean> = new Map()
  public sortingField = 'id_desc'
  public selectedLanguage$: Observable<string>
  public searchTerm$ = new Subject<string>()
  public accessRights = AccessRights
  public productEntities$: Observable<Dictionary<Product>>
  public organizationEntities$: Observable<Dictionary<Organization>>
  public serieProposalEntities$: Observable<Dictionary<SerieProposal>>
  public memberEntities$: Observable<Dictionary<Member>>
  public serieProposalStatus = SerieProposalStatus
  public mfPages = 1
  public mfRowsOnPage = 25

  constructor(
    private store: Store<AppState>,
    public serieProposalService: SerieProposalService,
    private router: Router,
    public messageService: MessageService
  ) {}

  ngOnInit() {
    this.selectedLanguage$ = this.store.select(
      settingSelectors.selectById,
      'language'
    )

    this.productEntities$ = this.store.select(productSelectors.selectEntities)
    this.organizationEntities$ = this.store.select(
      organizationSelectors.selectEntities
    )
    this.serieProposalEntities$ = this.store.select(
      serieProposalSelectors.selectEntities
    )
    this.memberEntities$ = this.store.select(memberSelectors.selectEntities)
  }

  public onRowClicked(row: SerieProposal) {
    this.onClickRow.emit(row)
  }

  public onMessageClicked(row: SerieProposal) {
    this.router.navigate(['/bookings/serie-proposals', row.id, 'messages'])
  }

  public onCheckRow(targetRow: SerieProposal) {
    this.checkedIds.set(
      targetRow.id,
      !Boolean(this.checkedIds.get(targetRow.id))
    )
  }

  public onToggleAllChecked() {
    this.allCheckedToggle = !this.allCheckedToggle

    this.dataTable.data.forEach((row) =>
      this.checkedIds.set(row.id, this.allCheckedToggle)
    )
  }

  public onClickBulkAction(bulkOption: string) {
    const rows: SerieProposal[] = this.dataTable.data.filter((x) =>
      this.checkedIds.get(x.id)
    )

    if (rows.length > 0) {
      this.onBulkAction.emit({ action: bulkOption, items: rows })
    }
  }

  public onSearch(value) {
    this.onSearchTerm.emit(value)
  }

  public onSortfieldClick(fieldName: string) {
    this.sortingField =
      this.sortingField == `${fieldName}_asc`
        ? `${fieldName}_desc`
        : `${fieldName}_asc`

    this.field = this.sortingField.split('_')[0]
    this.sorted = this.sortingField.split('_')[1]

    this.onSorted.emit(this.sortingField)
  }

  public getCategory$(serieProposal: SerieProposal): Observable<string> {
    return combineLatest([
      this.store.select(memberSelectors.selectEntities),
      this.store.select(organizationSelectors.selectEntities)
    ]).pipe(
      map(([memberEntities, organizationEntities]) => {
        const member = memberEntities[serieProposal?.customerPersonId]
        const organization = organizationEntities[serieProposal?.organizationId]

        const category = organization?.category ?? member?.category ?? null
        if (!category) {
          return null
        }

        return `category${category}desc`
      })
    )
  }

  public selectPage(event) {
    this.onSelectedPage.emit(event)
  }

  public setSearchLimit(event) {
    this.onSelectLimit.emit(event)
  }

  public getSerieProposalCounterProposalText(
    serieProposal: SerieProposal
  ): Observable<string> {
    return this.store.select(serieProposalItemSelectors.selectAll).pipe(
      map((serieProposalItems) => {
        if (
          serieProposalItems.find(
            (item) =>
              item.serieProposalId == serieProposal.id &&
              item.status == SerieProposalItemStatus.COUNTER_PROPOSAL
          )
        ) {
          return 'serieProposalModule.awaitingCustomerApproval'
        } else {
          return 'serieProposalModule.awaitingAdminAction'
        }
      })
    )
  }

  getDaysText(item: SerieProposal): Observable<string> {
    if (!item.details?.repeatableBookings?.length) {
      return of('')
    }

    return this.selectedLanguage$.pipe(
      map((language) => {
        const locale = language || 'en'
        const dayNum = item.details.repeatableBookings[0].dayOfWeek

        if (dayNum == null) {
          return ''
        }

        const date = moment().isoWeekday(dayNum).toDate()

        const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' })
        return formatter.format(date)
      })
    )
  }
}
